import { defineStore } from "pinia";

import { MerchantApiError } from "../authentication/errors.js";
import {
  defaultNotificationFilters,
  defaultPreferenceDraft,
  deliveryFailureFor,
  type MerchantAttentionNotification,
  isMandatoryRule,
  merchantVisiblePreferenceRules,
  preferenceDraftFromRecord,
  recordFromDraft,
  validatePreferenceDraft,
  type MerchantNotification,
  type NotificationDeliveryFailure,
  type NotificationListFilters,
  type NotificationPreferenceDraft,
  type NotificationPreferenceRecord,
  type NotificationPreferenceRule,
} from "./models.js";
import type { NotificationsService } from "./service.js";

export const useNotificationsStore = defineStore("merchant-notifications", {
  state: (): {
    errorMessage?: string;
    attentionErrorMessage?: string;
    attentionItems: MerchantAttentionNotification[];
    attentionLoading: boolean;
    attentionNextCursor: string | null;
    attentionUnreadCount: number;
    failedCount: number;
    deliveryRetryIds: string[];
    filters: NotificationListFilters;
    listErrorMessage?: string;
    loading: boolean;
    mutationMessage?: string;
    nextCursor: string | null;
    notifications: MerchantNotification[];
    preferenceDraft: NotificationPreferenceDraft;
    preferenceRecord?: NotificationPreferenceRecord;
    retryingNotificationId?: string;
    savingPreferences: boolean;
    service?: NotificationsService;
    unreadCount: number;
    validationErrors: string[];
  } => ({
    deliveryRetryIds: [],
    attentionItems: [],
    attentionLoading: false,
    attentionNextCursor: null,
    attentionUnreadCount: 0,
    failedCount: 0,
    filters: defaultNotificationFilters(),
    loading: false,
    nextCursor: null,
    notifications: [],
    preferenceDraft: defaultPreferenceDraft(),
    savingPreferences: false,
    unreadCount: 0,
    validationErrors: [],
  }),
  getters: {
    deliveryFailures: (state): NotificationDeliveryFailure[] =>
      state.notifications.flatMap((notification) => {
        const failure = deliveryFailureFor(
          notification,
          state.deliveryRetryIds.includes(notification.notificationId),
        );
        return failure === undefined ? [] : [failure];
      }),
    visiblePreferenceRules: (state): NotificationPreferenceRule[] =>
      merchantVisiblePreferenceRules(state.preferenceDraft.rules),
    unreadLabel: (state): string => `${state.unreadCount} unread notifications`,
  },
  actions: {
    configure(service: NotificationsService): void {
      this.service = service;
    },
    async loadAll(filters?: NotificationListFilters): Promise<void> {
      const effectiveFilters = filters ?? this.filters;
      this.filters = effectiveFilters;
      this.loading = true;
      delete this.errorMessage;
      delete this.listErrorMessage;
      try {
        const [pageResult, preferencesResult] = await Promise.allSettled([
          this.requireService().loadNotifications(effectiveFilters),
          this.requireService().loadPreferences(),
        ]);

        if (preferencesResult.status === "rejected") {
          throw preferencesResult.reason;
        }

        const preferences = preferencesResult.value;
        if (pageResult.status === "fulfilled") {
          this.applyNotificationPage(pageResult.value);
        } else {
          this.applyNotificationPage(emptyNotificationPage());
          this.listErrorMessage = messageForError(
            pageResult.reason,
            "Recent notifications could not be loaded.",
          );
        }

        this.preferenceRecord = preferences;
        this.preferenceDraft = preferenceDraftFromRecord(preferences);
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Notifications could not be loaded.");
      } finally {
        this.loading = false;
      }
    },
    async loadAttentionNotifications(loadMore = false): Promise<void> {
      const cursor = loadMore ? this.attentionNextCursor : undefined;
      if (loadMore && cursor === null) return;

      this.attentionLoading = true;
      delete this.attentionErrorMessage;
      try {
        const page = await this.requireService().loadAttentionNotifications(
          50,
          cursor ?? undefined,
        );
        this.attentionItems = loadMore
          ? appendUniqueAttentionNotifications(this.attentionItems, page.items)
          : [...page.items];
        this.attentionNextCursor = page.nextCursor;
        this.attentionUnreadCount = this.attentionItems.filter((item) => !item.isRead).length;
      } catch (error: unknown) {
        this.attentionErrorMessage = messageForError(
          error,
          "Attention notifications could not be loaded.",
        );
      } finally {
        this.attentionLoading = false;
      }
    },
    async markAttentionRead(notificationId: string): Promise<void> {
      const current = this.attentionItems.find((item) => item.notificationId === notificationId);
      if (current === undefined || current.isRead) return;

      delete this.attentionErrorMessage;
      try {
        const updated = await this.requireService().markAttentionRead(notificationId);
        this.applyAttentionNotification(updated);
        this.attentionUnreadCount = this.attentionItems.filter((item) => !item.isRead).length;
      } catch (error: unknown) {
        this.attentionErrorMessage = messageForError(
          error,
          "The attention notification could not be marked read.",
        );
      }
    },
    async markAllAttentionRead(): Promise<void> {
      for (const item of this.attentionItems.filter((notification) => !notification.isRead)) {
        await this.markAttentionRead(item.notificationId);
        if (this.attentionErrorMessage !== undefined) return;
      }
    },
    async markRead(notificationId: string): Promise<void> {
      delete this.errorMessage;
      try {
        const notification = await this.requireService().markRead(notificationId);
        this.applyNotification(notification);
        this.recalculateCounts();
        this.mutationMessage = "Notification marked read.";
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Notification could not be marked read.");
      }
    },
    async markAllRead(): Promise<void> {
      const unread = this.notifications.filter((notification) => notification.status !== "READ");
      for (const notification of unread) {
        await this.markRead(notification.notificationId);
      }
    },
    async retryDelivery(notificationId: string): Promise<void> {
      this.validationErrors = validatePreferenceDraft(this.preferenceDraft);
      if (this.validationErrors.length > 0) {
        this.errorMessage = "Fix notification preference errors before retrying delivery.";
        return;
      }

      this.retryingNotificationId = notificationId;
      delete this.errorMessage;
      try {
        const notification = await this.requireService().retryDelivery(notificationId);
        this.deliveryRetryIds = Array.from(new Set([...this.deliveryRetryIds, notificationId]));
        this.applyNotification(notification);
        this.recalculateCounts();
        this.mutationMessage = "Notification delivery retry was queued.";
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Notification delivery could not be retried.");
      } finally {
        delete this.retryingNotificationId;
      }
    },
    async savePreferences(): Promise<void> {
      this.validationErrors = validatePreferenceDraft(this.preferenceDraft);
      if (this.validationErrors.length > 0) {
        delete this.errorMessage;
        delete this.mutationMessage;
        return;
      }

      this.savingPreferences = true;
      try {
        delete this.errorMessage;
        delete this.mutationMessage;
        this.preferenceRecord = await this.requireService().savePreferences(
          recordFromDraft(this.preferenceRecord, this.preferenceDraft),
        );
        this.preferenceDraft = preferenceDraftFromRecord(this.preferenceRecord);
        this.mutationMessage = "Notification preferences saved.";
      } catch (error: unknown) {
        this.errorMessage = messageForError(error, "Notification preferences could not be saved.");
      } finally {
        this.savingPreferences = false;
      }
    },
    setRule(rule: NotificationPreferenceRule, enabled: boolean): void {
      if (isMandatoryRule(rule.eventType, rule.channel) && !enabled) {
        this.validationErrors = [
          `${rule.eventType} ${rule.channel} is mandatory and cannot be disabled.`,
        ];
        return;
      }
      this.preferenceDraft.rules = this.preferenceDraft.rules.map((item) =>
        sameRule(item, rule) ? { ...item, enabled } : item,
      );
      this.validationErrors = [];
    },
    applyNotification(notification: MerchantNotification): void {
      this.notifications = this.notifications.map((item) =>
        item.notificationId === notification.notificationId ? notification : item,
      );
    },
    applyAttentionNotification(notification: MerchantAttentionNotification): void {
      this.attentionItems = this.attentionItems.map((item) =>
        item.notificationId === notification.notificationId ? notification : item,
      );
    },
    applyNotificationPage(page: {
      failedCount: number;
      nextCursor: string | null;
      notifications: MerchantNotification[];
      unreadCount: number;
    }): void {
      this.notifications = page.notifications;
      this.nextCursor = page.nextCursor;
      this.unreadCount = page.unreadCount;
      this.failedCount = page.failedCount;
    },
    recalculateCounts(): void {
      this.unreadCount = this.notifications.filter(
        (notification) => notification.status !== "READ",
      ).length;
      this.failedCount = this.notifications.filter(
        (notification) => notification.status === "FAILED",
      ).length;
    },
    requireService(): NotificationsService {
      if (this.service === undefined) {
        throw new Error("Notifications store was not configured before use.");
      }
      return this.service;
    },
  },
});

export { defaultNotificationFilters, isMandatoryRule };

function sameRule(left: NotificationPreferenceRule, right: NotificationPreferenceRule): boolean {
  return (
    left.eventType === right.eventType &&
    left.channel === right.channel &&
    left.scope === right.scope
  );
}

function emptyNotificationPage(): {
  failedCount: number;
  nextCursor: string | null;
  notifications: MerchantNotification[];
  unreadCount: number;
} {
  return {
    failedCount: 0,
    nextCursor: null,
    notifications: [],
    unreadCount: 0,
  };
}

function appendUniqueAttentionNotifications(
  current: MerchantAttentionNotification[],
  incoming: readonly MerchantAttentionNotification[],
): MerchantAttentionNotification[] {
  const currentIds = new Set(current.map((item) => item.notificationId));
  return [...current, ...incoming.filter((item) => !currentIds.has(item.notificationId))];
}

function messageForError(error: unknown, fallback: string): string {
  if (error instanceof MerchantApiError && error.kind === "access")
    return "You do not have permission to manage notifications.";
  if (error instanceof MerchantApiError && error.kind === "validation") return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}
